import { useCallback, useEffect, useState } from "react";
import { logDataToConsole, logError } from "../utils/customLogger";

interface UseFileToBase64Return {
  base64Urls: string[];
  isConverting: boolean;
  error: string | null;
  convertFilesToBase64: (files: File[]) => Promise<string[]>;
  clearBase64Urls: () => void;
}

/**
 * Converts one or more browser `File`s into base64 data-URL strings.
 *
 * Designed for the "convert -> send to backend" pattern where the consumer
 * already has the files in memory (e.g. from an `<input type="file" />`).
 *
 * Behavior:
 *   - `convertFilesToBase64(files)` reads every file via `FileReader.readAsDataURL`
 *     in parallel and resolves to the resulting `data:...;base64,...` URLs.
 *     The same list is also stashed in state so consumers can read
 *     `base64Urls` after conversion without holding onto the promise.
 *   - `autoConvert` (default `true`) triggers conversion automatically when
 *     the `files` array passed to the hook changes; set it to `false` if
 *     you want fully on-demand behavior via `convertFilesToBase64`.
 *   - `clearBase64Urls()` empties the cached URLs and clears any error so
 *     callers can release the memory after they've consumed the buffers.
 *   - Individual file read failures are logged via {@link logError} and the
 *     hook resolves with an empty array, surfacing the failure via `error`.
 *
 * @param files       Optional initial list of files. Only used when
 *                    `autoConvert` is true.
 * @param autoConvert When true, the hook converts `files` automatically as
 *                    they change. Defaults to true.
 */
export const useFileToBase64 = (
  files?: File[],
  autoConvert: boolean = true,
): UseFileToBase64Return => {
  const [base64Urls, setBase64Urls] = useState<string[]>([]);
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const convertFileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };

      reader.onerror = (readerError) => {
        logError(
          new Error(`Failed to convert file ${file.name} to base64`),
          "useFileToBase64",
        );
        reject(readerError);
      };

      reader.readAsDataURL(file);
    });
  }, []);

  const convertFilesToBase64 = useCallback(
    async (filesToConvert: File[]): Promise<string[]> => {
      if (!filesToConvert || filesToConvert.length === 0) {
        setBase64Urls([]);
        return [];
      }

      setIsConverting(true);
      setError(null);

      try {
        logDataToConsole(
          `Starting conversion of ${filesToConvert.length} files to base64`,
        );

        const base64Promises = filesToConvert.map((file) =>
          convertFileToBase64(file),
        );

        const base64Results = await Promise.all(base64Promises);

        setBase64Urls(base64Results);

        logDataToConsole(
          `Successfully converted ${base64Results.length} files to base64`,
        );

        return base64Results;
      } catch (err) {
        const errorMessage = "Failed to convert files to base64";
        setError(errorMessage);
        logError(err as Error, "useFileToBase64 - convertFilesToBase64");
        setBase64Urls([]);
        return [];
      } finally {
        setIsConverting(false);
      }
    },
    [convertFileToBase64],
  );

  const clearBase64Urls = useCallback(() => {
    setBase64Urls([]);
    setError(null);
  }, []);

  useEffect(() => {
    if (autoConvert && files && files.length > 0) {
      convertFilesToBase64(files);
    }
  }, [files, autoConvert, convertFilesToBase64]);

  return {
    base64Urls,
    isConverting,
    error,
    convertFilesToBase64,
    clearBase64Urls,
  };
};
